import { Component, EventEmitter, inject, Input, NgZone, OnChanges, OnInit, Output, signal, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { Metaproduct, MetaproductComponent, ProductPrice } from '@scalefast/core';
import { AnalyticsStoreService } from '../../../shared/services';
import { CurrencyPipe, DecimalPipe, NgClass } from '@angular/common';
import { LocalizationConfigService } from '@scalefast/config-angular';
import { ProductHelper } from 'src/app/shared/helpers/product.helper';
import { AppOptionsMetaCrossPriceComponent } from './options-metaproduct-crossedprice.component';
import { GenericLogger } from '@scalefast/ecommerce-core';

@Component({
  selector: 'app-options-metaproduct',
  standalone: true,
  styleUrls: ['./options-metaproduct.component.scss'],
  templateUrl: './options-metaproduct.component.html',
  imports: [NgClass, DecimalPipe, CurrencyPipe, AppOptionsMetaCrossPriceComponent],
})
export class AppOptionsMetaproductComponent implements OnInit, OnChanges, MetaproductComponent {
  @Input() productId: string | undefined;
  @Input() metaproducts!: Metaproduct[];
  @Input() isProductPage = true;
  @Input() showDropdown = false;
  @Input() showOptionPrice: boolean = false;
  @Input() feedId: string | string[] | undefined;
  @Output() optionChanged = new EventEmitter<string>(false);

  #logger = inject(GenericLogger);
  selectedOption: string = '';
  optionsMetaproducts: Metaproduct = {
    type: 'options',
    options: [],
  };
  dropdownOpen = signal(false);
  readonly currency;

  constructor(
    private router: Router,
    private zone: NgZone,
    private analyticsStoreService: AnalyticsStoreService,
    private localizationConfigService: LocalizationConfigService,
  ) {
    this.currency = this.localizationConfigService.get().currency;
  }

  ngOnInit(): void {
    this.setMetaproducts();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['productId']) {
      this.setMetaproducts();
    }
    if (changes.metaproducts) {
      this.setMetaproducts();
    }
  }

  toggleDropdown() {
    this.dropdownOpen.update((isOpen) => !isOpen);
  }

  setMetaproducts(): void {
    this.optionsMetaproducts = this.metaproducts[0];

    if (this.optionsMetaproducts?.options.length) {
      this.optionsMetaproducts.options = ProductHelper.getSortedMetaproductsByLabel(this.optionsMetaproducts, this.#logger);
    }

    this.setSelectedOption();
  }

  setSelectedOption() {
    if (this.optionsMetaproducts?.options) {
      if (this.isProductPage) {
        this.selectedOption = this.optionsMetaproducts?.parentProduct?.id ?? '';
      } else {
        const { options } = this.optionsMetaproducts;
        const matchingOption = options.find((option) => option.id === this.productId);
        if (matchingOption) {
          this.selectedOption = matchingOption.id ?? '';
        }
      }
    }
  }

  redirectToOptionSelected(productId: string) {
    // Product page case:
    if (this.isProductPage) {
      this.zone.run(() => {
        this.router.navigate(['/product', productId]);
      });
    } else {
      // Product list case
      this.selectedOption = productId;
      this.optionChanged.emit(productId);
    }
    if (this.showDropdown) {
      this.dropdownOpen.update((isOpen) => !isOpen);
    }
  }

  getOptionPriceInfo(data: any): ProductPrice {
    return {
      currency: this.currency,
      country: '',
      prices: [],
      currencies: [
        {
          currency: this.currency,
          prices: [
            {
              licence: '',
              oldPrice: data.crossedPrice ? parseFloat(data.crossedPrice) : undefined,
              price: parseFloat(data.price),
            },
          ],
        },
      ],
      includesTaxes: true,
      taxType: '',
    } as ProductPrice;
  }

  gtmEvent(event: any): void {
    const category = event.currentTarget.getAttribute('data-gtmcategory');
    const action = event.currentTarget.getAttribute('data-gtmaction');
    const label = event.currentTarget.getAttribute('data-gtmlabel');
    this.analyticsStoreService.miscEvent(category, action, label);
  }
}
